import React, { useEffect, useState } from "react";
import { FaCheckCircle, FaFileAlt, FaPrint, FaIdCard } from "react-icons/fa";
import apiRequest from "../services/api";
import logo from "../images/moh-logo.png";
import QRCode from "qrcode";

const Apply = () => {
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const userStatus = storedUser?.status;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isDateAllowed, setIsDateAllowed] = useState(false);
  const [isDepAndCollEnabled, setIsDepAndCollEnabled] = useState(
    Boolean(storedUser?.college?.status && storedUser?.department?.status)
  );
  const [slip, setSlip] = useState(null);
  const [round, setRound] = useState(null);
  const [centers, setCenters] = useState([]);
  const [centerId, setCenterId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  const [showCenterModal, setShowCenterModal] = useState(false);
  const [changingCenter, setChangingCenter] = useState(false);
  const [newCenterId, setNewCenterId] = useState("");

  const generateQR = async (id) => {
    try {
      const url = `https://hple.moh.gov.et/admin/users/${id}/check`;
      const qr = await QRCode.toDataURL(url);
      setQrCodeUrl(qr);
    } catch (err) {
      console.error("QR Error", err);
    }
  };

  const fetchSlip = async () => {
    try {
      const data = await apiRequest(`users/slip`);
      if (data.success && data.data) {
        setSlip(data.data);

        generateQR(data.data.id);
        return true;
      }

      return false;
    } catch (err) {}
  };

  const fetchActiveRound = async () => {
    const data = await apiRequest(`users/round`);
    if (data.success) {
      const currentRound = data.data;
      setRound(currentRound);
    }
  };

  const fetchCenters = async () => {
    const data = await apiRequest(`users/centers`);
    if (data.success) setCenters(data.data);
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);

      try {
        const registered = await fetchSlip();

        // ALWAYS load centers
        await fetchCenters();

        if (!registered) {
          await fetchActiveRound();
        }
      } catch (err) {
        setError("No active registration announcement found.");
      } finally {
        setLoading(false);
      }

      if (!isDepAndCollEnabled) {
        setError(
          "\n Your Institution or Department not allowed for this round"
        );
        return;
      }
    };

    init();
  }, []);

  const handleApply = async (e) => {
    e.preventDefault();
    if (!round) return;

    setError(""); // Clear previous errors
    setSuccessMsg("");

    try {
      setIsSubmitting(true);
      const data = await apiRequest(
        `users/apply?roundId=${round.id}&centerId=${centerId}`,
        { method: "POST" }
      );

      if (data.success) {
        setSuccessMsg("Application successful.");

        // RE-FETCH the full slip to ensure all nested relations (college, dept) are loaded
        await fetchSlip();
      } else {
        setError(data.message || "Application failed");
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCenterChange = async (e) => {
    e.preventDefault();
    if (!round) return;

    setError(""); // Clear previous errors
    setSuccessMsg("");

    try {
      setIsSubmitting(true);
      const data = await apiRequest(
        `users/change-center?centerId=${centerId}`,
        { method: "POST" }
      );

      if (data.success) {
        setSuccessMsg("Center changed successfully.");

        // RE-FETCH the full slip to ensure all nested relations (college, dept) are loaded
        await fetchSlip();
      } else {
        setError(data.message || "Request failed");
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="p-10 text-center font-bold text-sky-600">Loading...</div>
    );

  return (
    <div className="max-w-5xl mx-auto p-6">
      <style>
        {`
          @media print {
            body * { visibility: hidden !important; }
            .printable-slip, .printable-slip * { visibility: visible !important; }
            .printable-slip { 
              position: absolute !important; 
              left: 0 !important; 
              top: 0 !important; 
              width: 100% !important;
              padding: 20px !important;
              border: none !important;
            }
            .no-print { display: none !important; }
          }
        `}
      </style>

      {slip ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between no-print">
            <div className="flex items-center gap-3">
              <FaCheckCircle className="text-green-500 text-3xl" />
              <h2 className="text-2xl font-extrabold text-slate-800">
                Registration Complete
              </h2>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setNewCenterId(slip?.center?.id || "");
                  setShowCenterModal(true);
                }}
                className="bg-amber-500 hover:bg-amber-600 text-white px-5 py-2.5 rounded-xl font-bold transition"
              >
                Change Center
              </button>

              <button
                onClick={() => window.print()}
                className="bg-sky-700 hover:bg-sky-800 text-white px-6 py-2.5 rounded-xl font-bold transition"
              >
                <FaPrint />
              </button>
            </div>
          </div>

          <div className="printable-slip bg-white border border-slate-200 shadow-2xl rounded-3xl p-4">
            {/* Header */}
            <div className="flex items-center justify-center gap-4 border-b-2 border-sky-900 pb-6 mb-8 text-center">
              <img src={logo} alt="MOH Logo" className="w-16 h-16" />
              <div>
                <h1 className="text-xl font-black text-slate-900 uppercase">
                  Federal Ministry of Health
                </h1>
                <p className="text-[10px] font-bold text-sky-800 tracking-widest uppercase">
                  Ethiopian Health Professionals Licensing Examination
                </p>
              </div>
            </div>

            <h3 className="text-center text-lg font-black underline mb-1 decoration-sky-900">
              EXAM ADMISSION SLIP
            </h3>

            {/* Photo and QR Row */}
            <div className="flex justify-between items-end mb-8 border-b border-slate-100 pb-8 px-4">
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-36 border-2 border-slate-200 rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center">
                  {slip.photoUrl ? (
                    <img
                      src={slip.photoUrl}
                      alt="Candidate"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FaIdCard className="text-slate-200 text-5xl" />
                  )}
                </div>
              </div>

              <div className="flex flex-col items-center gap-2">
                <div className="p-1 border border-slate-200 rounded-lg">
                  {qrCodeUrl && (
                    <img src={qrCodeUrl} alt="QR Code" className="w-24 h-24" />
                  )}
                </div>
                <span className="text-[8px] font-bold text-slate-400 uppercase">
                  Scan to Verify
                </span>
              </div>
            </div>

            {/* Data Rows */}
            <div className="grid grid-cols-1 divide-y divide-slate-100 px-4">
              <SlipRow label="Full Name" value={slip.name} />
              <SlipRow label="Gender" value={slip.sex} />
              <SlipRow label="Fayda No. / FAN" value={slip.username} />
              <SlipRow label="Institution" value={slip.college?.description} />
              <SlipRow
                label="Department"
                value={slip.department?.description}
              />
              <SlipRow label="Exam Center" value={slip.center?.name} />
              <SlipRow
                label="Exam Round"
                value={`${slip.round?.name} (${
                  slip.round?.from
                    ? new Date(slip.round.from).toLocaleDateString()
                    : ""
                } - ${
                  slip.round?.to
                    ? new Date(slip.round.to).toLocaleDateString()
                    : ""
                })`}
              />
            </div>

            <div className="mt-10 pt-4 border-t border-dashed border-slate-200 flex justify-between items-center text-[9px] text-slate-400 font-bold italic">
              <span>Generated: {new Date().toLocaleString()}</span>
              <span>EHPLE System Official Document</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-3xl shadow-2xl border border-sky-100 p-8">
          <h2 className="text-2xl font-extrabold text-slate-800 mb-6">
            Exam Application
          </h2>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-xl flex items-center gap-3 animate-shake">
              <div className="bg-red-500 text-white rounded-full p-1 shadow-sm">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <p className="text-red-700 text-sm font-bold tracking-wide">
                {error}
              </p>
            </div>
          )}

          <form onSubmit={handleApply} className="space-y-5">
            <div>
              <label className="text-xs font-bold text-sky-700 uppercase">
                Active Exam Round
              </label>
              <input
                disabled
                value={round?.name || ""}
                className="w-full mt-1 bg-slate-100 border-2 border-slate-200 px-4 py-3 rounded-xl"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-sky-700 uppercase">
                Exam Center
              </label>
              <select
                required
                value={centerId}
                onChange={(e) => setCenterId(e.target.value)}
                className="w-full mt-1 bg-slate-50 border-2 border-slate-100 px-4 py-3 rounded-xl"
              >
                <option value="">-- Select Center --</option>
                {centers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Apply button is hidden when error exists */}
            {!error && (
              <button
                type="submit"
                disabled={isSubmitting || !round || !centerId}
                className={`w-full font-bold py-4 rounded-xl shadow-lg ${
                  isSubmitting || !round || !centerId
                    ? "bg-slate-300"
                    : "bg-sky-500 text-white"
                }`}
              >
                {isSubmitting ? "Submitting..." : "Apply for Exam"}
              </button>
            )}
          </form>
        </div>
      )}

      {showCenterModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 no-print">
          <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl p-6 animate-fadeIn">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-black text-slate-800">
                Change Exam Center
              </h3>

              <button
                onClick={() => setShowCenterModal(false)}
                className="text-slate-400 hover:text-slate-700 text-xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-sky-700 uppercase">
                  Current Center
                </label>

                <input
                  disabled
                  value={slip?.center?.name || ""}
                  className="w-full mt-1 bg-slate-100 border-2 border-slate-200 px-4 py-3 rounded-xl"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-sky-700 uppercase">
                  Select New Center
                </label>

                <select
                  value={newCenterId}
                  onChange={(e) => setNewCenterId(e.target.value)}
                  className="w-full mt-1 bg-slate-50 border-2 border-slate-100 px-4 py-3 rounded-xl"
                >
                  <option value="">-- Select Center --</option>

                  {centers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-semibold">
                  {error}
                </div>
              )}

              {successMsg && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm font-semibold">
                  {successMsg}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowCenterModal(false)}
                  className="px-5 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 font-bold"
                >
                  Cancel
                </button>

                <button
                  disabled={
                    changingCenter ||
                    !newCenterId ||
                    Number(newCenterId) === slip?.center?.id
                  }
                  onClick={async () => {
                    try {
                      setChangingCenter(true);
                      setError("");
                      setSuccessMsg("");

                      const data = await apiRequest(
                        `users/change-center?centerId=${newCenterId}`,
                        {
                          method: "POST",
                        }
                      );

                      if (data.success) {
                        setSuccessMsg("Center changed successfully.");

                        await fetchSlip();

                        setTimeout(() => {
                          setShowCenterModal(false);
                        }, 1000);
                      }
                    } catch (err) {
                      setError(err.message || "Failed to change center");
                    } finally {
                      setChangingCenter(false);
                    }
                  }}
                  className={`px-5 py-3 rounded-xl font-bold text-white transition ${
                    changingCenter ||
                    !newCenterId ||
                    Number(newCenterId) === slip?.center?.id
                      ? "bg-slate-300"
                      : "bg-amber-500 hover:bg-amber-600"
                  }`}
                >
                  {changingCenter ? "Changing..." : "Change Center"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SlipRow = ({ label, value }) => (
  <div className="flex py-3">
    <span className="w-1/3 text-[9px] font-black text-slate-400 uppercase tracking-widest">
      {label}
    </span>
    <span className="w-2/3 text-xs font-bold text-slate-800">
      {value || "---"}
    </span>
  </div>
);

export default Apply;
